<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <button v-if="canModify" @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Syllabus
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredSyllabus"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search syllabus..."
      empty-icon="clipboard"
      empty-title="No syllabus found"
      empty-description="Add your first syllabus to get started"
    >
      <template #filters>
        <select v-model="filterCourse" class="filter-select-modern">
          <option value="">All Courses</option>
          <option v-for="course in courseOptions" :key="course.id" :value="course.id">{{ course.name }}</option>
        </select>
      </template>

      <template #cell-title="{ item }">
        <span class="fw-medium">{{ item.title }}</span>
      </template>

      <template #cell-course="{ item }">
        <span class="text-muted">{{ item.courseName }}</span>
      </template>

      <template #cell-topics="{ item }">
        <span class="badge bg-info">{{ item.topicCount }} topics</span>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', item.status === 'published' ? 'status-active' : 'status-inactive']">
          {{ item.status }}
        </span>
      </template>

      <template #cell-updated="{ item }">
        <span class="text-muted">{{ formatDate(item.updatedAt) }}</span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button 
            v-if="canModify"
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit syllabus'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            v-if="canModify"
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete syllabus', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Add First Syllabus
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Syllabus' : 'Add Syllabus'"
      icon="clipboard"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.title || !form.courseId"
      :item-id="isEditing ? editingId : ''"
      @submit="saveSyllabus"
    >
      <form @submit.prevent="saveSyllabus" class="modal-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Title <span class="text-danger">*</span></label>
            <input 
              v-model="form.title" 
              type="text" 
              class="form-control" 
              placeholder="e.g., Mathematics Syllabus"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Course <span class="text-danger">*</span></label>
            <select v-model="form.courseId" class="form-control" required>
              <option value="">Select Course</option>
              <option v-for="course in courseOptions" :key="course.id" :value="course.id">
                {{ course.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="2"
            placeholder="Brief description of the syllabus..."
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Topics</label>
          <div v-for="(topic, index) in form.topics" :key="index" class="topic-input-row">
            <span class="topic-number">{{ index + 1 }}</span>
            <input 
              v-model="topic.title" 
              type="text" 
              class="form-control" 
              placeholder="Topic title" 
            />
            <button 
              type="button" 
              class="btn btn-outline-danger btn-sm" 
              @click="removeTopic(index)" 
              v-if="form.topics.length > 1"
            >
              <vue-feather type="x" size="14" />
            </button>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="addTopic">
            <vue-feather type="plus" size="14" class="me-1" />
            Add Topic
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Status</label>
          <select v-model="form.status" class="form-control">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

export default {
  name: 'SyllabusList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isContentManager)
    const alert = useAlert()
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Syllabus', active: true }
    ]

    const columns = [
      { key: 'title', label: 'Title', sortable: true, minWidth: '250px' },
      { key: 'course', label: 'Course', sortable: true },
      { key: 'topics', label: 'Topics', class: 'text-center' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'updated', label: 'Updated', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const syllabusList = ref([])
    const courseOptions = ref([])
    const loading = ref(true)
    const filterCourse = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      title: '',
      courseId: '',
      description: '',
      status: 'draft',
      topics: [{ title: '' }]
    })

    const filteredSyllabus = computed(() => {
      if (!filterCourse.value) return syllabusList.value
      return syllabusList.value.filter(s => s.courseId === filterCourse.value)
    })

    const fetchSyllabus = async () => {
      loading.value = true
      try {
        const response = await api.get('/v1/admin/syllabus')
        syllabusList.value = response.data.syllabus
        courseOptions.value = response.data.courses
      } catch (err) {
        console.error('Failed to fetch syllabus:', err)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }

    const resetForm = () => {
      form.title = ''
      form.courseId = ''
      form.description = ''
      form.status = 'draft'
      form.topics = [{ title: '' }]
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (syllabus) => {
      isEditing.value = true
      editingId.value = syllabus.id
      form.title = syllabus.title || ''
      form.courseId = syllabus.courseId || ''
      form.description = syllabus.description || ''
      form.status = syllabus.status || 'draft'
      form.topics = syllabus.topics?.length ? syllabus.topics.map(t => ({ title: t.title || t })) : [{ title: '' }]
      showModal.value = true
    }

    const addTopic = () => form.topics.push({ title: '' })
    const removeTopic = (index) => form.topics.splice(index, 1)

    const saveSyllabus = async () => {
      if (!form.title || !form.courseId) return
      
      saving.value = true
      try {
        const payload = {
          title: form.title,
          courseId: form.courseId,
          description: form.description,
          status: form.status,
          topics: form.topics.filter(t => t.title.trim())
        }

        if (isEditing.value) {
          const response = await api.put(`/v1/admin/syllabus/${editingId.value}`, payload)
          const index = syllabusList.value.findIndex(s => s.id === editingId.value)
          if (index !== -1) {
            syllabusList.value[index] = { ...syllabusList.value[index], ...response.data.syllabus || payload }
          }
          alert.success('Updated!', 'Syllabus updated successfully')
        } else {
          const response = await api.post('/v1/admin/syllabus', payload)
          syllabusList.value.unshift(response.data.syllabus || response.data)
          alert.success('Created!', 'Syllabus created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save syllabus')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (item) => {
      const result = await alert.delete(
        'Delete Syllabus?',
        `Are you sure you want to delete "${item.title}"?`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/syllabus/${item.id}`)
          syllabusList.value = syllabusList.value.filter(s => s.id !== item.id)
          alert.success('Deleted!', 'Syllabus has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete syllabus')
        }
      }
    }

    onMounted(fetchSyllabus)

    return { 
      breadcrumbs, 
      columns,
      syllabusList, 
      courseOptions, 
      loading,
      filterCourse, 
      filteredSyllabus,
      showModal,
      isEditing,
      saving,
      form,
      canModify,
      openCreateModal,
      openEditModal,
      addTopic,
      removeTopic,
      saveSyllabus,
      formatDate, 
      confirmDelete 
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.topic-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.topic-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f1f5f9;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #64748b;
  flex-shrink: 0;
}

.topic-input-row .form-control {
  flex: 1;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .topic-number {
  background: #334155;
  color: #94a3b8;
}
</style>
